import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Form } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import useSWR from 'swr'
import { FileMagicNumber } from 'types/building'
import Utils from 'utils'

import useTourUploadImage, { TourUploadImagePath } from '../useTourUploadImage'

export default function useUpdateLogo({
  handleCancel
}: {
  handleCancel: () => void
}) {
  const { t } = useTranslation()
  const { id: buildingId } = useParams()
  const { directory } = useDirectory()
  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'
  const { data: buildingInfo, isLoading } = useSWR<Building>({
    url: `${apiPrefix}/buildings/${buildingId}`
  })
  const [form] = Form.useForm()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const [isLogoLinkHasErrors, setIsLogoLinkHasErrors] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChooseFile = () => inputRef?.current?.click()
  const initialThumbnail = form.getFieldValue('logo')

  const thumbnail = selectedFile
    ? URL.createObjectURL(selectedFile)
    : initialThumbnail

  const logoLink = Form.useWatch('logoLink', form)
  const logo = Form.useWatch('logo', form)
  const isFormValid = logoLink && logo && !isLogoLinkHasErrors

  const { trigger: updateLogo, isMutating: updating } = useMutation(
    `${apiPrefix}/buildings/${buildingId}/update-extra-info`
  )

  const { trigger: deleteLogo, isMutating: deleting } = useMutation(
    `${apiPrefix}/buildings/${buildingId}/update-extra-info`,
    {},
    true
  )

  const { uploadFile } = useTourUploadImage({
    buildingId: Number(buildingId),
    path: TourUploadImagePath.LOGO,
    selectedFile
  })

  /**
   * Saves the logo by uploading the selected file and updating the building's extra info.
   * If a logo is provided, it uploads the file using the `uploadFile` function.
   * Upon successful upload, it updates the building's logo and logo link using the `updateLogo` mutation.
   * Displays a success message and closes the modal on successful update.
   *
   * @param values - The form values containing the logo and logoLink.
   */
  const handleSaveLogo = async (values: any) => {
    const { logoLink, logo } = values
    if (logo) {
      await uploadFile({
        onSuccess: (key) => {
          const dataUpdate = {
            logo: key,
            logoLink
          }
          updateLogo({
            method: 'patch',
            data: dataUpdate,
            successMessage: t('building.saved')
          }).then(() => {
            // closed modal when update logo success
            handleCancel()
          })
        }
      })
    }
  }

  /**
   * Deletes the logo by patching the building's extra info with an empty logo key.
   * Displays a success message and closes the modal on successful deletion.
   */
  const handleDeleteLogo = () => {
    deleteLogo({
      method: 'patch',
      data: {
        logo: '',
        logoLink: ''
      },
      successMessage: t('building.deleted')
    }).then(() => {
      // closed modal when delete logo success
      handleCancel()
    })
  }

  /**
   * Handles the file input change event for updating the logo.
   * Validates and processes the selected file to update the form's logo field.
   * If the file is valid and of the correct type, sets the file as the selected logo
   * and updates the form field without errors.
   * If validation fails or the file type is incorrect, resets the logo field and displays
   * appropriate error messages.
   *
   * @param event - The change event triggered by the file input.
   */

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target
    const resetForm = () => {
      setSelectedFile(null)
      form.resetFields(['logo'])
    }

    if (files?.length) {
      form.setFields([{ name: 'logoWrapper', errors: [] }])

      Utils.validateFile(files[0], t)
        .then(() => {
          Utils.getFileMagicNumber(files[0], FileMagicNumber.IMAGE)
            .then(() => {
              setSelectedFile(files[0])
              form.setFieldValue('logo', files[0])
              form.setFields([{ name: 'logo', errors: [] }])
            })
            .catch(() => {
              form.setFields([
                {
                  name: 'logoWrapper',
                  errors: [t('translation.brokenFileMessage')]
                },
                { name: 'logo', value: '' }
              ])
              setSelectedFile(null)
            })
        })
        .catch((error: any) => {
          form.setFields([
            {
              name: 'logoWrapper',
              errors: [error.message]
            },
            { name: 'logo', value: '' }
          ])
          setSelectedFile(null)
          form.setFieldValue('logo', undefined)
        })
      return
    }

    resetForm()
  }

  const initialFormValue = useMemo(() => {
    return buildingInfo
      ? {
          logo: buildingInfo.extraInfo?.logoUrl,
          logoLink: buildingInfo.extraInfo?.logoLink
        }
      : null
  }, [buildingInfo])

  useEffect(() => {
    if (initialFormValue) {
      form.setFieldsValue(initialFormValue)
    }
  }, [form, initialFormValue])

  return {
    form,
    logoLink,
    inputRef,
    thumbnail,
    isFormValid,
    selectedFile,
    buildingInfo,
    initialFormValue,
    isLogoLinkHasErrors,
    initialThumbnail,
    handleSaveLogo,
    setSelectedFile,
    setIsLogoLinkHasErrors,
    handleChooseFile,
    handleFileChange,
    handleDeleteLogo,
    isLoading,
    updating,
    deleting
  }
}
